import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemSafeHTMLTitle, ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {
  forksListPath,
  orgTopicReposPath,
  repoCommitActivityInsightsPath,
  repoIssuesPath,
  repoPullRequestsPath,
  repositoryParticipationPath,
  repositoryPath,
  repoStargazersPath,
} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {PrimaryLanguage, RepositoryItem} from '@github-ui/use-repos-list-results'
import {ObservableBox} from '@github-ui/use-sticky-header/ObservableBox'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Icon} from '@primer/octicons-react'
import {
  GearIcon,
  GitPullRequestIcon,
  IssueOpenedIcon,
  LawIcon,
  RepoCloneIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  StarIcon,
} from '@primer/octicons-react'
import {Label, Link, RelativeTime, Tooltip, Truncate} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {Dialog} from '@primer/react/experimental'
import type {SxProp} from '@primer/styled-react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {memo, useRef, useState} from 'react'
import {renderToString} from 'react-dom/server'

import {containsHtml} from '../utils/contains-html'
import {LanguageCircle} from './LanguageCircle'
import styles from './ReposListItem.module.css'
import {SearchMatchText} from './SearchMatchText'
import {Sparkline} from './Sparkline'

interface Props extends SxProp {
  repo: RepositoryItem
  showNameWithOwner: boolean
  query?: string
}

const MAX_TOPICS_WIDE_SCREEN = 10
const MAX_TOPICS_NARROW_SCREEN = 3

const sparklineWidth = 120
const tinySparklineWidth = 60

export const ReposListItem = memo((props: Props) => {
  const {variant} = useListViewVariant()

  if (variant === 'default') {
    return (
      <ListItem
        title={props.showNameWithOwner ? <DefaultItemTitleWithOwner {...props} /> : <DefaultItemTitle {...props} />}
      >
        <DefaultItemContent {...props} />
      </ListItem>
    )
  }

  return (
    <ListItem
      metadata={<CompactItemMetadata {...props} />}
      title={props.showNameWithOwner ? <CompactItemTitleWithOwner {...props} /> : <CompactItemTitle {...props} />}
      className={styles.ListItem_2}
      metadataContainerClassName={styles.ListItem_3}
    >
      <ListItemLeadingContent className={styles.ListItemLeadingContent_1}>
        <ListItemLeadingVisual icon={getRepoIcon(props.repo)} color="fg.muted" />
      </ListItemLeadingContent>
      <ListItemMainContent>
        <CompactWideItemContent {...props} />
        <CompactNarrowItemContent {...props} />
      </ListItemMainContent>
    </ListItem>
  )
})

ReposListItem.displayName = 'ReposListItem'

function DefaultItemContent({repo, query}: Props) {
  const showSettingsLink = useFeatureFlag('repos_relevance_page')

  const {description, lastUpdated, license, allTopics, isAdminableByUser} = repo
  return (
    <ListItemMainContent>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          color: 'fg.muted',
          gap: 2,
          width: ['100%', '100%', `calc(100% - ${sparklineWidth}px)`],
          pl: 2,
        }}
      >
        {description && (
          <ListItemDescription>
            <FormattedDescription description={description} query={query} className={styles.FormattedDescription_1} />
          </ListItemDescription>
        )}

        {allTopics.length > 0 && <TopicsLabels {...repo} />}

        <div className={styles.Box_6}>
          <LanguageLabel {...repo} />
          {repo.primaryLanguage && <span>•</span>}

          {license && (
            <>
              <LicenseLabel {...repo} />
              <span>•</span>
            </>
          )}

          <ForksLabel {...repo} />
          <span>•</span>

          <StarsLabel {...repo} />
          <span>•</span>

          {repo.issueCount !== null && (
            <>
              <IssuesLabel issueCount={repo.issueCount} owner={repo.owner} name={repo.name} />
              <span>•</span>
            </>
          )}

          {repo.pullRequestCount !== null && (
            <>
              <PullRequestsLabel pullRequestCount={repo.pullRequestCount} owner={repo.owner} name={repo.name} />
              <span>•</span>
            </>
          )}

          <span className={styles.Text_3}>
            {lastUpdated.hasBeenPushedTo ? 'Updated' : 'Created'} <RelativeTime datetime={lastUpdated.timestamp} />
          </span>

          <RelativeTime datetime={lastUpdated.timestamp} format="micro" className={styles.RelativeTime_1} />

          {showSettingsLink && isAdminableByUser && (
            <>
              <span>•</span>
              <RepoSettingsLink owner={repo.owner} name={repo.name} />
            </>
          )}
        </div>
      </Box>
    </ListItemMainContent>
  )
}

function CompactNarrowItemContent({repo, query}: Props) {
  const showSettingsLink = useFeatureFlag('repos_relevance_page')
  const {description, name, owner, isAdminableByUser, pullRequestCount} = repo

  return (
    <div className={styles.Box_7}>
      {description && (
        <ListItemDescription>
          <FormattedDescription description={description} query={query} className={styles.FormattedDescription_1} />
        </ListItemDescription>
      )}
      <div className={styles.Box_6}>
        <LanguageLabel {...repo} />
        {repo.primaryLanguage && <span>•</span>}

        <ForksLabel {...repo} />
        <span>•</span>

        <StarsLabel {...repo} />
        <span>•</span>

        {pullRequestCount !== null && (
          <PullRequestsLabel pullRequestCount={pullRequestCount} owner={owner} name={name} />
        )}

        {showSettingsLink && isAdminableByUser && (
          <>
            <span>•</span>
            <RepoSettingsLink owner={owner} name={name} />
          </>
        )}
      </div>
    </div>
  )
}

function CompactWideItemContent({repo, query}: Props) {
  const {description} = repo
  return (
    <ListItemDescription className={clsx(styles.ListItemDescription_1, styles.ListItemDescription_2)}>
      <Truncate inline title={description} className={styles.Truncate_2}>
        <FormattedDescription description={description} query={query} />
      </Truncate>
    </ListItemDescription>
  )
}

function DefaultItemTitle(props: Props) {
  const {name, type, owner} = props.repo

  return (
    <ListItemTitle
      value={name}
      href={repositoryPath({owner, repo: name})}
      headingClassName={clsx(styles.ListItemTitle_6, styles.ListItemTitle_10)}
      containerClassName={clsx(styles.ListItemTitle_7, styles.ListItemTitle_11)}
    >
      <Label variant="secondary" data-listview-item-visibility-label>
        {type}
      </Label>
      <ParticipationSparkline {...props} />
    </ListItemTitle>
  )
}

function CompactItemTitle({repo}: Props) {
  const {name, type, owner} = repo

  return (
    <ListItemTitle
      value={name}
      href={repositoryPath({owner, repo: name})}
      headingClassName={styles.ListItemTitle_8}
      containerClassName={clsx(styles.ListItemTitle_9, styles.ListItemTitle_11)}
    >
      <Label variant="secondary" className={styles.Label_2}>
        {type}
      </Label>
    </ListItemTitle>
  )
}

function DefaultItemTitleWithOwner(props: Props) {
  const {name, type, owner} = props.repo
  const searchMatchName = props.query ? <SearchMatchText query={props.query}>{name}</SearchMatchText> : name
  const searchMatchOwner = props.query ? <SearchMatchText query={props.query}>{owner}</SearchMatchText> : owner
  const repoUrl = repositoryPath({owner, repo: name})

  return (
    <ListItemSafeHTMLTitle
      html={renderToString(<NwoTitleLink name={searchMatchName} owner={searchMatchOwner} href={repoUrl} />)}
      headingClassName={clsx(styles.ListItemTitle_6, styles.ListItemTitle_10)}
      containerClassName={clsx(styles.ListItemTitle_7, styles.ListItemTitle_11)}
    >
      <Label variant="secondary">{type}</Label>
      <ParticipationSparkline {...props} />
    </ListItemSafeHTMLTitle>
  )
}

function CompactItemTitleWithOwner({repo, query}: Props) {
  const {name, type, owner} = repo
  const searchMatchName = query ? <SearchMatchText query={query}>{name}</SearchMatchText> : name
  const searchMatchOwner = query ? <SearchMatchText query={query}>{owner}</SearchMatchText> : owner
  const repoUrl = repositoryPath({owner, repo: name})

  return (
    <ListItemSafeHTMLTitle
      html={renderToString(<NwoTitleLink name={searchMatchName} owner={searchMatchOwner} href={repoUrl} />)}
      headingClassName={styles.ListItemTitle_8}
      containerClassName={clsx(styles.ListItemTitle_9, styles.ListItemTitle_11)}
    >
      <Label variant="secondary" className={styles.Label_2}>
        {type}
      </Label>
    </ListItemSafeHTMLTitle>
  )
}

function CompactItemMetadata({repo}: Props) {
  const showSettingsLink = useFeatureFlag('repos_relevance_page')
  return (
    <>
      <ListItemMetadata className={clsx(styles.ListItemMetadata_3, styles.ListItemMetadata_5)}>
        <LanguageLabel truncate {...repo} />
      </ListItemMetadata>
      {showSettingsLink && (
        <ListItemMetadata className={styles.compactItemSettingsLink}>
          {repo.isAdminableByUser && <RepoSettingsLink owner={repo.owner} name={repo.name} />}
        </ListItemMetadata>
      )}
      <ListItemMetadata className={clsx(styles.ListItemMetadata_4, styles.ListItemMetadata_5)}>
        <ForksLabel {...repo} />
      </ListItemMetadata>
      <ListItemMetadata className={clsx(styles.ListItemMetadata_4, styles.ListItemMetadata_5)}>
        <StarsLabel {...repo} />
      </ListItemMetadata>
    </>
  )
}

function getRepoIcon(repo: RepositoryItem) {
  const {isFork, type} = repo
  const lowerCaseType = type.toLowerCase()

  let icon
  if (isFork) {
    icon = RepoForkedIcon
  } else if (lowerCaseType.includes('mirror')) {
    icon = RepoCloneIcon
  } else if (lowerCaseType.includes('private') || lowerCaseType.includes('internal')) {
    icon = RepoLockedIcon
  } else {
    icon = RepoIcon
  }

  return icon
}

function pluralizeTopic(count: number) {
  return count === 1 ? 'topic' : 'topics'
}

function TopicLabel({topic, owner, className}: {topic: string; owner: string; className?: string}) {
  return (
    <Link key={topic} href={orgTopicReposPath({topic, org: owner})} data-testid={`${topic}-topic-link`}>
      <Label size="large" className={clsx(styles.Label_3, className)}>
        {topic}
      </Label>
    </Link>
  )
}

function MoreTopicsButton({
  notShown,
  setIsOpen,
  className,
}: {
  notShown: number
  className?: string
  setIsOpen: (value: boolean) => void
}) {
  return notShown > 0 ? (
    <Label
      as="button"
      aria-label={`Show ${notShown} more ${pluralizeTopic(notShown)}`}
      className={clsx(styles.Label_4, className)}
      size="large"
      variant="secondary"
      onClick={() => setIsOpen(true)}
    >
      + {notShown}
    </Label>
  ) : null
}

function TopicsLabels({allTopics, owner, name}: {allTopics: string[]; owner: string; name: string}) {
  const [isOpen, setIsOpen] = useState(false)

  const narrowTopicNames = allTopics.slice(0, MAX_TOPICS_NARROW_SCREEN)
  const narrowTopicsNotShown = allTopics.length - MAX_TOPICS_NARROW_SCREEN

  const wideOnlyTopicNames = allTopics.slice(MAX_TOPICS_NARROW_SCREEN, MAX_TOPICS_WIDE_SCREEN)
  const topicsNotShown = Math.max(0, allTopics.length - MAX_TOPICS_WIDE_SCREEN)

  return (
    <div className={styles.Box_8}>
      {narrowTopicNames.map(topic => (
        <TopicLabel key={topic} topic={topic} owner={owner} />
      ))}
      <MoreTopicsButton notShown={narrowTopicsNotShown} setIsOpen={setIsOpen} className={styles.hideOnSmallScreen} />
      {wideOnlyTopicNames.map(topic => (
        <TopicLabel key={topic} topic={topic} owner={owner} className={styles.smallScreenOnly} />
      ))}
      <MoreTopicsButton notShown={topicsNotShown} setIsOpen={setIsOpen} className={styles.smallScreenOnly} />
      {isOpen && (
        <Dialog
          title={`Topics for ${owner}/${name}`}
          width="small"
          position={{narrow: 'bottom', wide: 'center'}}
          footerButtons={[{content: 'Done', onClick: () => setIsOpen(false)}]}
          onClose={() => setIsOpen(false)}
        >
          <div className={styles.Box_8}>
            {allTopics.map(topic => (
              <Link
                key={topic}
                href={orgTopicReposPath({topic, org: owner})}
                data-testid={`dialog-${topic}-topic-link`}
              >
                <Label size="large" className={styles.Label_3}>
                  {topic}
                </Label>
              </Link>
            ))}
          </div>
        </Dialog>
      )}
    </div>
  )
}

function LanguageLabel({primaryLanguage, truncate}: {primaryLanguage: PrimaryLanguage | null; truncate?: boolean}) {
  if (!primaryLanguage) {
    return null
  }

  return (
    <div className={styles.Box_9}>
      <LanguageCircle color={primaryLanguage.color} />
      {truncate ? (
        <Truncate inline title={primaryLanguage.name} className={styles.Truncate_3}>
          {primaryLanguage.name}
        </Truncate>
      ) : (
        <span className={styles.Text_4}>{primaryLanguage.name}</span>
      )}
    </div>
  )
}

function LicenseLabel({license}: {license: string | null}) {
  if (!license) {
    return null
  }

  return <IconLabel content={license} icon={LawIcon} />
}

function StarsLabel({starsCount, owner, name}: {starsCount: number; owner: string; name: string}) {
  const count = human(starsCount, {precision: getSignificanceBasedPrecision(starsCount)})
  const unit = count === '1' ? 'star' : 'stars'
  const label = `${count} ${unit}`
  return <IconLabel aria-label={label} content={count} icon={StarIcon} href={repoStargazersPath(owner, name)} />
}

function ForksLabel({forksCount, owner, name}: {forksCount: number; owner: string; name: string}) {
  const count = human(forksCount, {precision: getSignificanceBasedPrecision(forksCount)})
  const unit = count === '1' ? 'fork' : 'forks'
  const label = `${count} ${unit}`
  return (
    <IconLabel aria-label={label} content={count} icon={RepoForkedIcon} href={forksListPath({owner, repo: name})} />
  )
}

function IssuesLabel({issueCount, owner, name}: {issueCount: number; owner: string; name: string}) {
  const count = human(issueCount, {precision: getSignificanceBasedPrecision(issueCount)})
  const unit = count === '1' ? 'issue' : 'issues'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'issue-count'}
      content={count}
      icon={IssueOpenedIcon}
      href={repoIssuesPath(owner, name)}
    />
  )
}

function PullRequestsLabel({pullRequestCount, owner, name}: {pullRequestCount: number; owner: string; name: string}) {
  const count = human(pullRequestCount, {precision: getSignificanceBasedPrecision(pullRequestCount)})
  const unit = count === '1' ? 'pull request' : 'pull requests'
  const label = `${count} ${unit}`
  return (
    <IconLabel
      aria-label={label}
      data-testid={'pull-request-count'}
      content={count}
      icon={GitPullRequestIcon}
      href={repoPullRequestsPath(owner, name)}
    />
  )
}

function IconLabel({
  icon,
  href,
  content,
  ...props
}: {
  icon: Icon
  href?: string
  content: string
  ['aria-label']?: string
}) {
  if (href) {
    return (
      <Link {...props} muted href={href} className={styles.Link_1}>
        <Octicon icon={icon} />
        {content}
      </Link>
    )
  }

  return (
    <div {...props} className={styles.Link_1}>
      <Octicon icon={icon} />
      {content}
    </div>
  )
}

function ParticipationSparkline({repo, sx}: Props) {
  const [participation, setParticipation] = useState(repo.participation)
  const isFetchingRef = useRef(false)

  async function fetchParticipation() {
    if (participation || isFetchingRef.current) return
    isFetchingRef.current = true

    const response = await verifiedFetchJSON(repositoryParticipationPath({owner: repo.owner, repo: repo.name}))
    if (response.ok) {
      const bodyText = await response.text()
      const json = bodyText.length > 0 ? JSON.parse(bodyText) : []
      setParticipation(json)
    }
  }

  const [observeRow, unobserveRow] = useIntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) {
      fetchParticipation()
    }
  })

  return participation ? (
    participation.length === 0 ? (
      <div data-testid="empty-repo-sparkline" />
    ) : (
      <Tooltip text={`${repo.owner}/${repo.name}’s past year of commit activity`}>
        <Box
          sx={{
            ...sx,
          }}
          className={styles.Box_10}
        >
          <Link
            className="d-block"
            href={repoCommitActivityInsightsPath({owner: repo.owner, repo: repo.name})}
            aria-label={`${repo.owner}/${repo.name}'s past year of commit activity`}
            target="_blank"
          >
            <div className={styles.smallScreenOnly}>
              <Sparkline uniqueKey={`${repo.owner}/${repo.name}`} points={participation} width={sparklineWidth} />
            </div>
            <div className={styles.hideOnSmallScreen} data-listview-item-tiny-sparkline>
              <Sparkline
                uniqueKey={`${repo.owner}/${repo.name}#tiny`}
                points={participation}
                width={tinySparklineWidth}
              />
            </div>
          </Link>
        </Box>
      </Tooltip>
    )
  ) : (
    <ObservableBox role="presentation" onObserve={observeRow} onUnobserve={unobserveRow} />
  )
}

function FormattedDescription({
  description,
  query,
  className,
}: {
  description: SafeHTMLString
  query: string | undefined
  className?: string
}) {
  const canHighlight = !!query && !containsHtml(description)
  const searchMatchDescription = canHighlight
    ? (renderToString(<SearchMatchText query={query}>{description}</SearchMatchText>) as SafeHTMLString)
    : description

  return (
    <VerifiedHTMLText
      // `repos-list-description` class is required to support `Link underlines` user setting
      className={clsx('repos-list-description', className)}
      html={searchMatchDescription}
    />
  )
}

function NwoTitleLink({name, owner, href}: {name: ReactNode; owner: ReactNode; href: string}) {
  return (
    <Link className={styles.ListItemTitleLink} href={href} data-component="listview-title-link">
      <span className={styles.RepoOwner}>{owner}/</span>
      <span>{name}</span>
    </Link>
  )
}

function RepoSettingsLink({owner, name}: {owner: string; name: string}) {
  return (
    <Link muted href={`${repositoryPath({owner, repo: name})}/settings`} aria-label="Repository settings">
      <GearIcon />
    </Link>
  )
}

try{ DefaultItemContent.displayName ||= 'DefaultItemContent' } catch {}
try{ CompactNarrowItemContent.displayName ||= 'CompactNarrowItemContent' } catch {}
try{ CompactWideItemContent.displayName ||= 'CompactWideItemContent' } catch {}
try{ DefaultItemTitle.displayName ||= 'DefaultItemTitle' } catch {}
try{ CompactItemTitle.displayName ||= 'CompactItemTitle' } catch {}
try{ DefaultItemTitleWithOwner.displayName ||= 'DefaultItemTitleWithOwner' } catch {}
try{ CompactItemTitleWithOwner.displayName ||= 'CompactItemTitleWithOwner' } catch {}
try{ CompactItemMetadata.displayName ||= 'CompactItemMetadata' } catch {}
try{ TopicLabel.displayName ||= 'TopicLabel' } catch {}
try{ MoreTopicsButton.displayName ||= 'MoreTopicsButton' } catch {}
try{ TopicsLabels.displayName ||= 'TopicsLabels' } catch {}
try{ LanguageLabel.displayName ||= 'LanguageLabel' } catch {}
try{ LicenseLabel.displayName ||= 'LicenseLabel' } catch {}
try{ StarsLabel.displayName ||= 'StarsLabel' } catch {}
try{ ForksLabel.displayName ||= 'ForksLabel' } catch {}
try{ IssuesLabel.displayName ||= 'IssuesLabel' } catch {}
try{ PullRequestsLabel.displayName ||= 'PullRequestsLabel' } catch {}
try{ IconLabel.displayName ||= 'IconLabel' } catch {}
try{ ParticipationSparkline.displayName ||= 'ParticipationSparkline' } catch {}
try{ FormattedDescription.displayName ||= 'FormattedDescription' } catch {}
try{ NwoTitleLink.displayName ||= 'NwoTitleLink' } catch {}
try{ RepoSettingsLink.displayName ||= 'RepoSettingsLink' } catch {}